// src/components/Resume/ResumePreview.jsx
import React from 'react';
import { ResumeFactory } from '../../templates/industries';
import { industrySections } from '../../data/industrySections';

const ResumePreview = ({ 
  data, 
  industry, 
  activeTab, 
  coverLetterData,
  formData,
  sections
}) => {
  // Get industry-specific sections - try multiple sources
  let industrySectionsData = sections;
  if (!industrySectionsData || industrySectionsData.length === 0) {
    if (industry && industrySections[industry]) {
      industrySectionsData = industrySections[industry].sections || [];
    }
  }

  // Get industry info
  const getIndustryInfo = () => {
    if (!industry) return null;
    const config = industrySections[industry];
    if (config) {
      return {
        id: industry,
        name: config.name || industry,
        icon: config.icon || '📄'
      };
    }
    return { id: industry, name: industry, icon: '📄' };
  };

  const industryInfo = getIndustryInfo();

  // Merge all data
  const mergedData = {
    ...data,
    ...formData,
    personalInfo: {
      full_name: data?.full_name || formData?.full_name || '',
      email: data?.email || formData?.email || '',
      phone: data?.phone || formData?.phone || '',
      address: data?.address || formData?.address || '',
      photo_url: data?.photo_url || formData?.photo_url || '',
      title: data?.title || formData?.title || '',
      summary: data?.summary || formData?.summary || '',
    },
    skills: data?.skills || formData?.skills || [],
    experience: data?.experience || formData?.experience || [],
    education: data?.education || formData?.education || [],
    certifications: data?.certifications || formData?.certifications || [],
    languages: data?.languages || formData?.languages || [],
  };

  // ============================================================
  // COVER LETTER VIEW
  // ============================================================
  if (activeTab === 'cover-letter' && coverLetterData?.body) {
    return (
      <div 
        id="resume-preview"
        style={{
          fontFamily: 'Arial, sans-serif',
          maxWidth: '800px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: '24px', 
          backgroundColor: '#1a1a2e', 
          color: 'white' 
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
            Cover Letter
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '14px', opacity: 0.8 }}>
            Generated with Fast Global Resume
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <p style={{ color: '#374151', fontSize: '14px' }}>
            {coverLetterData.recipient_name 
              ? `Dear ${coverLetterData.recipient_name},`
              : 'Dear Hiring Manager,'}
          </p>

          {coverLetterData.company_name && (
            <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>
              <strong>Company:</strong> {coverLetterData.company_name}
            </p>
          )}

          {coverLetterData.position && (
            <p style={{ color: '#6b7280', fontSize: '13px' }}>
              <strong>Position:</strong> {coverLetterData.position}
            </p>
          )}

          <div style={{ marginTop: '20px', lineHeight: '1.8' }}>
            {coverLetterData.body.split('\n\n').map((paragraph, idx) => (
              <p key={idx} style={{ color: '#374151', fontSize: '14px', marginBottom: '12px' }}>
                {paragraph}
              </p>
            ))}
          </div>

          <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
            <p style={{ color: '#374151', fontSize: '14px', margin: 0 }}>
              {coverLetterData.closing || 'Sincerely'},
            </p>
            <p style={{ color: '#1a1a2e', fontSize: '14px', fontWeight: 'bold', marginTop: '8px' }}>
              {data?.full_name || formData?.full_name || 'Your Name'}
            </p>
            {data?.email && (
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0' }}>
                {data.email}
              </p>
            )}
            {data?.phone && (
              <p style={{ color: '#6b7280', fontSize: '12px', margin: '2px 0 0' }}>
                {data.phone}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '12px 24px', 
          backgroundColor: '#f9fafb', 
          textAlign: 'center',
          fontSize: '11px',
          color: '#9ca3af',
          borderTop: '1px solid #e5e7eb'
        }}>
          Generated with Fast Global Resume • Professional resume builder for global careers
        </div>
      </div>
    );
  }

  // ============================================================
  // INDUSTRY-SPECIFIC RESUME VIEW
  // ============================================================
  if (industry) {
    return (
      <div 
        id="resume-preview"
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <ResumeFactory 
          industry={industryInfo}
          sections={industrySectionsData}
          data={mergedData}
          activeTab={activeTab}
          coverLetterData={coverLetterData}
        />
      </div>
    );
  }

  // ============================================================
  // FALLBACK: GENERIC RESUME
  // ============================================================
  return (
    <div 
      id="resume-preview"
      style={{
        fontFamily: 'Arial, sans-serif',
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      {/* Header */}
      <div style={{ 
        padding: '24px', 
        backgroundColor: '#1a1a2e', 
        color: 'white' 
      }}>
        {mergedData.personalInfo?.photo_url && (
          <img 
            src={mergedData.personalInfo.photo_url} 
            alt="Profile" 
            style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              objectFit: 'cover',
              border: '3px solid white',
              marginBottom: '12px'
            }}
          />
        )}
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
          {mergedData.personalInfo?.full_name || 'Your Name'}
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: '16px', opacity: 0.9 }}>
          {mergedData.personalInfo?.title || 'Professional Resume'}
        </p>
      </div>

      {/* Contact Info */}
      <div style={{ 
        padding: '12px 24px', 
        backgroundColor: '#f9fafb', 
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        fontSize: '13px',
        color: '#4b5563'
      }}>
        {mergedData.personalInfo?.email && <span>📧 {mergedData.personalInfo.email}</span>}
        {mergedData.personalInfo?.phone && <span>📞 {mergedData.personalInfo.phone}</span>}
        {mergedData.personalInfo?.address && <span>📍 {mergedData.personalInfo.address}</span>}
      </div>

      {/* Summary */}
      {mergedData.personalInfo?.summary && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' }}>
            Professional Summary
          </h2>
          <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>
            {mergedData.personalInfo.summary}
          </p>
        </div>
      )}

      {/* Experience */}
      {mergedData.experience && mergedData.experience.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' }}>
            Work Experience
          </h2>
          {mergedData.experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: '12px', paddingLeft: '12px', borderLeft: '3px solid #1a1a2e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '14px' }}>{exp.title}</strong>
                <span style={{ color: '#6b7280', fontSize: '13px' }}>{exp.company}</span>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '12px', margin: '2px 0' }}>
                {exp.startDate} - {exp.endDate || 'Present'}
              </p>
              {exp.description && (
                <p style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>
                  {exp.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {mergedData.education && mergedData.education.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '12px' }}>
            Education
          </h2>
          {mergedData.education.map((edu, idx) => (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <strong style={{ fontSize: '14px' }}>{edu.degree}</strong>
              <span style={{ color: '#6b7280', fontSize: '13px', marginLeft: '8px' }}>
                {edu.institution}
              </span>
              <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                ({edu.year})
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {mergedData.skills && mergedData.skills.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' }}>
            Skills
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {mergedData.skills.map((skill, idx) => (
              <span 
                key={idx} 
                style={{ 
                  backgroundColor: '#f3f4f6', 
                  padding: '4px 12px', 
                  borderRadius: '16px',
                  fontSize: '12px',
                  color: '#374151'
                }}
              >
                {typeof skill === 'object' ? skill.name : skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications */}
      {mergedData.certifications && mergedData.certifications.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' }}>
            Certifications
          </h2>
          {mergedData.certifications.map((cert, idx) => (
            <div key={idx} style={{ marginBottom: '6px', fontSize: '13px' }}>
              <strong>{cert.name}</strong>
              {cert.issuer && <span style={{ color: '#6b7280', marginLeft: '8px' }}>{cert.issuer}</span>}
              {cert.year && <span style={{ color: '#9ca3af', marginLeft: '8px' }}>({cert.year})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Languages */}
      {mergedData.languages && mergedData.languages.length > 0 && (
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' }}>
            Languages
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px' }}>
            {mergedData.languages.map((lang, idx) => (
              <span key={idx}>
                {typeof lang === 'object' ? `${lang.name} - ${lang.proficiency || ''}` : lang}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        padding: '12px 24px', 
        backgroundColor: '#f9fafb', 
        textAlign: 'center',
        fontSize: '11px',
        color: '#9ca3af',
        borderTop: '1px solid #e5e7eb'
      }}>
        Generated with Fast Global Resume • Professional resume builder for global careers
      </div>
    </div>
  );
};

export default ResumePreview;